export const createAuditProcessAlertForError = (errorData = {}) => {
  const code = errorData.code || 'createAuditProcessFailed';
  const message = errorData.message
    || (code === 'auditConfigurationIncomplete'
      ? 'Project audit configuration is not complete.'
      : code === 'invalidAuditFlagging'
        ? 'Integrated/combined audit requires more than one project standard.'
        : code === 'auditProcessAlreadyExists'
          ? 'Audit Process already exists for this project.'
          : 'Failed to create Audit Process.');

  return {
    type: code === 'auditProcessAlreadyExists' ? 'info' : 'danger',
    message,
  };
};

const valueText = (value) => (value == null ? '' : String(value));

export const isSustainabilityAuditProject = (project = {}) => {
  const sections = project.project_sections || project.projectSections || [];
  return sections.some((section) => {
    const sectionText = [section?.type, section?.serviceName, section?.service_name]
      .map(valueText)
      .join(' ')
      .toLowerCase();
    if (sectionText.includes('sustainability') || sectionText.includes('iscc')) return true;

    return (section?.services || []).some((service) => {
      const serviceText = [
        service?.standard,
        service?.standardName,
        service?.serviceName,
        service?.service_name,
        service?.product_type,
        service?.service_item,
        ...(service?.sub_services || []).flatMap((subService) => [
          subService?.standard,
          subService?.product_type,
          subService?.service_item,
        ]),
      ].map(valueText).join(' ').toLowerCase();
      return serviceText.includes('sustainability') || serviceText.includes('iscc');
    });
  });
};

export const isApplicableAuditService = (serviceName) => {
  const normalized = valueText(serviceName).trim().toLowerCase();
  return normalized.includes('certification') || normalized.includes('sustainability');
};

export const requiresProposedAuditDate = ({
  sectionType,
  serviceName,
  standard,
  productType,
  serviceItem,
} = {}) => {
  const auditServiceName = serviceName || sectionType;
  if (!isApplicableAuditService(auditServiceName)) {
    return false;
  }

  const itemText = [
    standard,
    productType,
    serviceItem,
  ].map(valueText).join(' ').toLowerCase();

  // CMF and fee rows are commercial/configuration lines, not audit cycles.
  // ISCC proposals include License Fee and Certification Fee rows alongside
  // Certification/Surveillance/Recertification, and those fee rows do not
  // receive proposed audit dates.
  return !itemText.includes('cmf') && !itemText.includes('fee');
};

export const buildProjectAuditReadiness = (projectData = {}) => {
  const sections = projectData.project_sections || projectData.projectSections || [];
  const rootStandards = [];
  const missingProposedAuditDates = [];

  sections.forEach((section) => {
    const sectionType = section.type || section.serviceName || section.service_name;
    (section.services || []).forEach((service) => {
      const standard = service.standard || service.standardName || service.standard_name || '';
      if (standard) {
        rootStandards.push(standard);
      }

      const row = {
        sectionType,
        serviceName: service.serviceName || service.service_name || sectionType,
        standard,
        productType: service.product_type || service.productCategoryName || service.productCategory || '',
        serviceItem: service.service_item || service.serviceItemName || service.serviceItem || '',
      };
      if (requiresProposedAuditDate(row) && !valueText(service.proposeDate || service.proposed_date).trim()) {
        missingProposedAuditDates.push({
          standard: row.standard || '-',
          productType: row.productType || '-',
          serviceItem: row.serviceItem || '-',
        });
      }

      (service.sub_services || service.productCategories || []).forEach((child) => {
        const childRow = {
          sectionType,
          serviceName: row.serviceName,
          standard,
          productType: child.product_type || child.productCategoryName || child.productCategory || '',
          serviceItem: child.service_item || child.serviceItemName || child.serviceItem || '',
        };
        if (requiresProposedAuditDate(childRow) && !valueText(child.proposeDate || child.proposed_date).trim()) {
          missingProposedAuditDates.push({
            standard: childRow.standard || '-',
            productType: childRow.productType || '-',
            serviceItem: childRow.serviceItem || '-',
          });
        }
      });
    });
  });

  const distinctRootStandardCount = new Set(rootStandards.map((standard) => valueText(standard).trim()).filter(Boolean)).size;
  const auditFlaggingType = valueText(projectData.auditFlaggingType || projectData.audit_flagging_type || 'none').toLowerCase();
  const requiresAuditFlagging = distinctRootStandardCount > 1
    && auditFlaggingType !== 'integrated'
    && auditFlaggingType !== 'combined';

  return {
    rootStandardCount: distinctRootStandardCount,
    missingProposedAuditDates,
    requiresAuditFlagging,
    ready: missingProposedAuditDates.length === 0 && !requiresAuditFlagging && distinctRootStandardCount > 0,
    waitingForAuditDate: missingProposedAuditDates.length > 0,
  };
};

export const createAuditProcessCtaState = ({
  projectId,
  auditConfigurationCompleted,
  creatingAuditProcess,
} = {}) => {
  if (!projectId) {
    return {
      disabled: true,
      title: 'Save the project first to enable Audit Process creation',
      helper: 'Audit Process creation will unlock once Audit Date and integrated/combined audit flags are valid for this project.',
    };
  }

  if (!auditConfigurationCompleted) {
    return {
      disabled: true,
      title: 'Complete the audit configuration above to enable this action',
      helper: 'Audit Process creation will unlock once Audit Date and integrated/combined audit flags are valid for this project.',
    };
  }

  return {
    disabled: Boolean(creatingAuditProcess),
    title: 'Create the initial Audit Process tasks for this project',
    helper: 'Audit Process creation is an explicit step. Click to generate the initial audit tasks (Application Review + Audit Scheduling).',
  };
};
